import AuthService from "../auth/auth.js";

class ArchivoDispositivoService {
  constructor() {
    this.baseURL = process.env.VUE_APP_API_URL || "https://efibox.efintel.cl";
    this.endpoint = "/dispositivos/api/archivos-dispositivo/";

    // 50MB para archivos técnicos (planos, manuales, documentación)
    this.maxFileSize = 50 * 1024 * 1024;
    this.allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
      "text/plain",
    ];

    // Tipos que NO se pueden previsualizar directamente en iframe
    this.tiposNoVisualizables = [
      "application/msword", // .doc (formato binario antiguo)
      "application/vnd.ms-excel", // .xls (formato binario antiguo)
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    ];
  }

  async subirArchivo(dispositivoId, archivo) {
    try {
      this.validarArchivo(archivo);

      if (!dispositivoId) {
        throw new Error("ID de dispositivo requerido");
      }

      const formData = new FormData();
      formData.append("archivo", archivo);
      formData.append("dispositivo", parseInt(dispositivoId));

      const tokens = this.obtenerTokens();

      const response = await fetch(`${this.baseURL}${this.endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
        body: formData,
      });

      const data = await this.procesarRespuesta(response);

      return this.mapearArchivo(data);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  async listarArchivos(dispositivoId) {
    try {
      const response = await AuthService.authenticatedRequest(`${this.endpoint}?dispositivo=${dispositivoId}`);

      let archivos = [];

      if (Array.isArray(response)) {
        archivos = response;
      } else if (response?.results) {
        archivos = response.results;
      }

      return archivos.map((archivo) => this.mapearArchivo(archivo));
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  async obtenerArchivoBlob(archivoId) {
    try {
      const tokens = this.obtenerTokens();

      const response = await fetch(`${this.baseURL}${this.endpoint}${archivoId}/ver/`, {
        method: "GET",
        credentials: "include",
        headers: {
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status} al obtener archivo`);
      }

      return await response.blob();
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  async descargarArchivo(archivoId, nombreArchivo) {
    try {
      const blob = await this.obtenerArchivoBlob(archivoId);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  async renombrarArchivo(archivoId, nuevoNombre) {
    try {
      if (!nuevoNombre || !nuevoNombre.trim()) {
        throw new Error("El nombre no puede estar vacío");
      }

      const tokens = this.obtenerTokens();
      const formData = new FormData();
      formData.append("nombre", nuevoNombre.trim());

      const response = await fetch(`${this.baseURL}${this.endpoint}${archivoId}/`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "X-CSRFToken": tokens.csrf || "",
          ...(tokens.jwt ? { Authorization: `Bearer ${tokens.jwt}` } : {}),
        },
        body: formData,
      });

      const data = await this.procesarRespuesta(response);

      return this.mapearArchivo(data);
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  async eliminarArchivo(archivoId) {
    try {
      await AuthService.authenticatedRequest(`${this.endpoint}${archivoId}/`, { method: "DELETE" });
    } catch (error) {
      throw this.manejarError(error);
    }
  }

  validarArchivo(archivo) {
    if (!archivo) {
      throw new Error("Archivo requerido");
    }

    if (archivo.size > this.maxFileSize) {
      const sizeMB = Math.round(this.maxFileSize / (1024 * 1024));
      throw new Error(`Archivo muy grande. Máximo ${sizeMB}MB`);
    }

    if (!this.allowedTypes.includes(archivo.type)) {
      throw new Error("Tipo de archivo no permitido");
    }
  }

  // Verificar si un archivo se puede previsualizar en iframe
  puedeVisualizarseEnIframe(tipoMime) {
    return !this.tiposNoVisualizables.includes(tipoMime);
  }

  // Determinar tipo MIME por extensión (fallback)
  obtenerTipoMimePorExtension(nombreArchivo) {
    const extension = nombreArchivo.toLowerCase().split(".").pop();
    const mapaTipos = {
      pdf: "application/pdf",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      csv: "text/csv",
      txt: "text/plain",
    };
    return mapaTipos[extension] || "application/octet-stream";
  }

  mapearArchivo(archivoBackend) {
    const fecha = this.parsearFecha(archivoBackend.creado_en);
    const nombreArchivo = archivoBackend.nombre || this.extraerNombreArchivo(archivoBackend.archivo);
    const tipoMime = this.obtenerTipoMimePorExtension(nombreArchivo);

    return {
      id: archivoBackend.id,
      nombre: nombreArchivo,
      fecha: fecha.fecha,
      hora: fecha.hora,
      autor: archivoBackend.subido_por
        ? `${archivoBackend.subido_por.nombre} ${archivoBackend.subido_por.apellido}`
        : "Usuario desconocido",
      dispositivoId: archivoBackend.dispositivo,
      tipoMime: tipoMime,
      esVisualizable: this.puedeVisualizarseEnIframe(tipoMime),
    };
  }

  extraerNombreArchivo(url) {
    try {
      const partes = url.split("/");
      return decodeURIComponent(partes[partes.length - 1]);
    } catch {
      return "archivo";
    }
  }

  parsearFecha(fechaStr) {
    try {
      const fecha = new Date(fechaStr.replace(" ", "T"));

      return {
        fecha: fecha.toLocaleDateString("es-CL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }),
        hora: fecha.toLocaleTimeString("es-CL", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
    } catch {
      return { fecha: "-", hora: "-" };
    }
  }

  obtenerTokens() {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    };

    return {
      csrf: getCookie("csrftoken"),
      jwt: getCookie("iiotefintel_cookie"),
    };
  }

  async procesarRespuesta(response) {
    const text = await response.text();

    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(text);
      } catch {
        throw new Error(`Error del servidor (${response.status})`);
      }

      const mensaje = errorData.detail || errorData.error || "Error al procesar archivo";
      throw new Error(mensaje);
    }

    return JSON.parse(text);
  }

  manejarError(error) {
    const mensajes = {
      "Sesión expirada": "SESSION_EXPIRED",
      "No hay sesión activa": "NO_SESSION",
      401: "UNAUTHORIZED",
      403: "FORBIDDEN",
      404: "NOT_FOUND",
      "Failed to fetch": "NETWORK_ERROR",
    };

    let codigo = "UNKNOWN_ERROR";
    for (const [key, value] of Object.entries(mensajes)) {
      if (error.message?.includes(key)) {
        codigo = value;
        break;
      }
    }

    const errorProcesado = new Error(error.message || "Error desconocido");
    errorProcesado.codigo = codigo;
    errorProcesado.original = error;

    return errorProcesado;
  }
}

export default new ArchivoDispositivoService();
